// BUSINESS DESK — page principale /pro/desk : verrouillage PIN puis navigation par onglets.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DeskLock from "@/desk/DeskLock";
import DeskDashboard from "@/desk/DeskDashboard";
import DeskWizard from "@/desk/DeskWizard";
import DeskDocuments from "@/desk/DeskDocuments";
import DeskCustomers from "@/desk/DeskCustomers";
import DeskProfile from "@/desk/DeskProfile";
import { deskApi, getToken, clearToken } from "@/desk/deskApi";
import { applyCachedPalette, applyLogoTheme, clearPaletteCache, resetPalette } from "@/desk/deskTheme";
import "@/desk/desk.css";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TABS = [
  { id: "dashboard", icon: "📊", label: "Accueil" },
  { id: "docs", icon: "🗂️", label: "Documents" },
  { id: "new", icon: "＋", label: "", isNew: true },
  { id: "clients", icon: "👥", label: "Clients" },
  { id: "profile", icon: "⚙️", label: "Entreprise" },
];

export default function DeskPage() {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(!!getToken());
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [wizardKey, setWizardKey] = useState(0);

  const loadProfile = useCallback(() => {
    deskApi("/profile")
      .then((d) => setProfile(d.profile))
      .catch((e) => { if (e.code === "LOCKED") setUnlocked(false); });
  }, []);

  useEffect(() => {
    applyCachedPalette();
    return () => resetPalette();
  }, []);

  useEffect(() => {
    if (unlocked) loadProfile();
  }, [unlocked, loadProfile]);

  useEffect(() => {
    if (!profile) return;
    if (profile.logo_url) applyLogoTheme(`${API}${profile.logo_url}`);
    else clearPaletteCache();
  }, [profile]);

  const lock = () => {
    clearToken();
    setUnlocked(false);
    setProfile(null);
  };

  if (!unlocked) return <DeskLock onUnlocked={() => setUnlocked(true)} />;

  const goNew = () => { setWizardKey((k) => k + 1); setTab("new"); };

  return (
    <div className="desk-root" data-testid="desk-page">
      <header className="desk-header">
        <div className="desk-header-title" style={{ cursor: "pointer" }} onClick={() => setTab("dashboard")}>
          {profile?.logo_url
            ? <img src={`${API}${profile.logo_url}`} alt="" className="desk-header-logo" />
            : <div className="desk-header-logo-ph">{(profile?.display_name || "E").charAt(0)}</div>}
          <div>
            <div className="desk-header-name">{profile?.display_name || "Business Desk"}</div>
            <div className="desk-header-sub">Business Desk · AYA PRO</div>
          </div>
        </div>
        <button className="desk-lock-btn" data-testid="desk-back-pro-btn" onClick={() => navigate("/pro")}>← AYA PRO</button>
      </header>

      <div className="desk-container">
        {tab === "dashboard" && <DeskDashboard profile={profile} onNew={goNew} onOpenDocs={() => setTab("docs")} />}
        {tab === "new" && profile && <DeskWizard key={wizardKey} profile={profile} onDone={() => setTab("docs")} />}
        {tab === "docs" && <DeskDocuments />}
        {tab === "clients" && <DeskCustomers />}
        {tab === "profile" && profile && <DeskProfile profile={profile} onRefresh={loadProfile} onLock={lock} />}
      </div>

      <nav className="desk-nav" data-testid="desk-nav">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`${tab === t.id ? "active" : ""}`}
            data-testid={`desk-tab-${t.id}`}
            onClick={() => (t.isNew ? goNew() : setTab(t.id))}
          >
            <span className={`dn-icon ${t.isNew ? "dn-new" : ""}`}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
